import { useState, useMemo } from 'react'
import { Bell, ChevronDown, Flag, Star } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import CardOne from '@/components/common/card/CardOne'
import MainTopBar from '@/components/common/bar/MainTopBar'
import { useGetDailyMissionDateRangeQuery } from '@/hooks/queries/dailyMission'
import { useCompleteToggleMutation } from '@/hooks/queries/dailyMission/mutation'
import { useGetMyStepsByLatestBlock } from '@/hooks/queries/chat'
import { useGetLastMindset } from '@/hooks/queries/report'
import { useUserStore } from '@/store/userStore'
import type { MyStepItem, MyStepStatus } from '@/api/model/chatType'
import type { TypeDailyMissionItem } from '@/api/model/dailyMissionType'
import PullToRefresh from '@/components/common/refresh/PullToRefresh'
import qbeeImage from '@/assets/images/qbee-character/qbee-happy-transparent.png'
import { useTypeTestStore } from '@/store/typeTestStore'
import { getWeekDates } from '@/utils/dateUtils'
import { useNoticeList } from '@/hooks/queries/notice'
import { useScrollToTop } from '@/hooks/useScrollToTop'
import { useUserInfoQuery } from '@/hooks/queries/user'

const HomePage = () => {
  useScrollToTop()

  const WEEK_DAYS = ['월', '화', '수', '목', '금', '토', '일']
  const navigate = useNavigate()
  const userStore = useUserStore()
  const { data: userInfo } = useUserInfoQuery(userStore.userInfo.user_id)

  const queryClient = useQueryClient()

  const { data: myStepsByLatestBlock } = useGetMyStepsByLatestBlock(!!userInfo?.data.type16_id)
  const { data: lastMindset } = useGetLastMindset()
  const { data: noticeList } = useNoticeList()
  const { setTypeResultFrom } = useTypeTestStore()

  const weekDates = getWeekDates()
  const todayIndex = WEEK_DAYS.indexOf(new Date().toLocaleDateString('ko-KR', { weekday: 'short' }))
  const [selectedMission, setSelectedMission] = useState<string>(WEEK_DAYS[todayIndex] || '월')
  const [missionRange, setMissionRange] = useState<number>(0)

  // Date range API로 일주일 미션 데이터 한번에 호출
  const { data: weekMissionsData } = useGetDailyMissionDateRangeQuery({
    start_date: weekDates[0],
    end_date: weekDates[6]
  })

  // Date range API 응답을 날짜별로 그룹핑 (is_stored 제외)
  const missionsByDate = useMemo(() => {
    if (!weekMissionsData?.data?.missions) return {}

    const grouped: Record<string, TypeDailyMissionItem[]> = {}

    // 먼저 모든 날짜에 빈 배열 초기화
    weekDates.forEach(date => {
      grouped[date] = []
    })

    // API 응답 데이터를 날짜별로 그룹핑 (is_stored가 true인 미션 제외)
    weekMissionsData.data.missions.forEach((mission: TypeDailyMissionItem) => {
      // is_stored가 true인 미션은 제외
      if ('is_stored' in mission && mission.is_stored === true) {
        return
      }

      const date = mission.mission_date
      if (!grouped[date]) {
        grouped[date] = []
      }
      grouped[date].push(mission)
    })

    return grouped
  }, [weekMissionsData, weekDates])

  const getCompletionPercentage = (dayIndex: number) => {
    const date = weekDates[dayIndex]
    const missions = missionsByDate[date] || []

    if (missions.length === 0) return 0

    const completedCount = missions.filter(mission => mission.is_completed).length
    return completedCount / missions.length
  }

  const getSelectedDayMissions = () => {
    const selectedIndex = WEEK_DAYS.indexOf(selectedMission)
    const date = weekDates[selectedIndex]
    return missionsByDate[date] || []
  }

  // Question List 관련 로직
  const totalQuestions = myStepsByLatestBlock?.data?.steps?.length || 0

  // 진행중이거나 달성된 상담 개수 계산
  const activeQuestions =
    myStepsByLatestBlock?.data?.steps?.filter(
      (item: MyStepItem) => item.status === 'IN_PROGRESS' || item.status === 'COMPLETED'
    )?.length || 0

  // 표시할 항목 개수 결정 로직
  const getVisibleQuestions = () => {
    let endIndex = totalQuestions

    if (activeQuestions === 0) {
      // 진행중이거나 달성된 상담이 없는 경우: "진행중인 상담이 없어요" 메시지와 함께 5개 표시
      endIndex = Math.min(5, totalQuestions)
    } else if (activeQuestions <= 3) {
      // 달성 + 진행중이 3개 이하인 경우: 총 5개 표시
      endIndex = Math.min(5, totalQuestions)
    } else {
      // 달성 + 진행중이 3개 초과인 경우: 달성 + 진행중 + 2개 추가 (5개 넘어도 OK)
      endIndex = Math.min(activeQuestions + 2, totalQuestions)
    }

    return {
      questions: myStepsByLatestBlock?.data.steps.slice(0, endIndex) || [],
      startIndex: 0,
      endIndex
    }
  }

  const { questions: visibleQuestions, startIndex: _startIndex, endIndex } = getVisibleQuestions()

  // 다음 항목들 개수
  const remainingNextCount = Math.max(0, totalQuestions - endIndex)

  const getStatusColor = (status: MyStepStatus) => {
    return status === 'COMPLETED'
      ? 'bg-amber-100 text-amber-700'
      : status === 'IN_PROGRESS'
        ? 'bg-blue-100 text-blue-600'
        : 'bg-purple-100 text-purple-600'
  }

  const selectedDayMissions = getSelectedDayMissions()

  // 미션 완료 토글 mutation
  const completeToggleMutation = useCompleteToggleMutation({
    onSuccess: () => {
      // 성공 시 전체 주간 미션 데이터를 다시 불러오기
      // refetchWeekMissons()
      queryClient.invalidateQueries({
        queryKey: ['dailyMissionDateRange']
      })
      queryClient.invalidateQueries({
        queryKey: ['dailyMission']
      })
      queryClient.invalidateQueries({
        queryKey: ['missionCalendar']
      })
    }
  })

  const handleMissionClick = (missionId: number) => {
    navigate(`/mission/detail/${missionId}`)
  }

  const handleToggleComplete = (e: React.MouseEvent, dailyMissionId: number | null) => {
    e.stopPropagation()
    if (dailyMissionId) {
      completeToggleMutation.mutate({ daily_mission_id: dailyMissionId.toString() })
    }
  }

  const handleStartTypeCheck = () => {
    setTypeResultFrom('home')
    navigate('/type-test')
  }

  return (
    <>
      <MainTopBar
        leftSide={
          <div className='flex items-center gap-2'>
            <img src={qbeeImage} alt='main-character' className=' w-10 h-10 mt-1' />
            <h1 className='text-2xl font-bold text-amber-900'>qBee</h1>
          </div>
        }
        rightSide={
          <button
            onClick={() => navigate('/notice')}
            className='w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors relative'
          >
            {noticeList?.notices.some(val => !val.is_read) && (
              <div className=' absolute w-2 h-2 rounded-2xl bg-red-500 top-1 right-1' />
            )}

            <Bell className='w-5 h-5 text-amber-900' />
          </button>
        }
      />
      <PullToRefresh
        onRefresh={() => {
          window.location.reload()
        }}
      >
        <div className='min-h-screen p-3 overflow-y-hidden flex flex-col gap-3'>
          {/* 상담 리마인드 */}
          <div className='bg-white/90 backdrop-blur rounded-2xl p-4'>
            <p className='text-sm text-amber-900 leading-relaxed'>{lastMindset}</p>
          </div>

          {/* 이번주 미션 현황 */}
          <CardOne>
            {/* Mission Section */}
            <div className='flex items-center justify-between mb-4 px-[10px]'>
              <h5 className='text-base font-bold text-gray-800'>이번주 미션 현황</h5>
              <div className='flex items-center gap-2'>
                <span className='text-sm text-amber-600 font-medium' role='button' onClick={() => navigate('/mission')}>
                  모두 보기
                </span>
              </div>
            </div>

            {/* Week Days with Donut Charts */}
            <div className='flex justify-between mb-3'>
              {WEEK_DAYS.map((day, index) => {
                const percentage = getCompletionPercentage(index)
                const isSelected = selectedMission === day

                // SVG 원의 정확한 둘레 계산 (반지름 36인 원)
                const radius = 36
                const circumference = 2 * Math.PI * radius
                const strokeDasharray = `${percentage * circumference} ${circumference}`

                return (
                  <div key={`home-mission-${day}`} className='flex flex-col items-center gap-1'>
                    <button
                      onClick={() => setSelectedMission(day)}
                      className={`relative w-10 h-10 transition-all ${isSelected ? 'scale-110' : 'hover:scale-105'}`}
                    >
                      {/* Donut Chart SVG */}
                      <svg className='w-full h-full -rotate-90' viewBox='0 0 80 80'>
                        {/* Background circle */}
                        <circle
                          cx='40'
                          cy='40'
                          r={radius}
                          fill='none'
                          stroke={isSelected ? '#fef3c7' : '#f3f4f6'}
                          strokeWidth='8'
                        />
                        {/* Progress circle */}
                        <circle
                          cx='40'
                          cy='40'
                          r={radius}
                          fill='none'
                          stroke={percentage > 0 ? (isSelected ? '#f59e0b' : '#fbbf24') : 'transparent'}
                          strokeWidth='8'
                          strokeDasharray={strokeDasharray}
                          strokeLinecap='round'
                          className='transition-all duration-300'
                        />
                      </svg>
                      {/* Day text */}
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <span className={`text-xs font-medium ${isSelected ? 'text-amber-700' : 'text-gray-600'}`}>
                          {day}
                        </span>
                      </div>
                    </button>
                  </div>
                )
              })}
            </div>

            {/* Mission List */}
            <div className='space-y-1'>
              {missionRange > 0 &&
                selectedDayMissions.slice(0, missionRange).map(mission => (
                  <div
                    key={`home-mission-${mission.mission_id}`}
                    className='flex items-center justify-between gap-3 px-3 py-1 rounded-xl hover:bg-amber-50 transition-colors cursor-pointer'
                    onClick={() => handleMissionClick(mission.mission_id)}
                  >
                    <div className='flex items-center gap-2 flex-1'>
                      <Flag className={`w-5 h-5 ${mission.is_completed ? 'text-gray-400' : 'text-amber-500'}`} />
                      <p
                        className={`flex-1 text-sm ${mission.is_completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}
                      >
                        {mission.name}
                      </p>
                    </div>
                    <button
                      onClick={e => handleToggleComplete(e, mission.daily_mission_id)}
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        mission.is_completed ? 'bg-gray-400 border-gray-400' : 'border-amber-500 hover:border-amber-400'
                      }`}
                    >
                      {mission.is_completed && (
                        <span className={`text-xs ${mission.is_completed ? 'text-white' : 'text-amber-500'}`}>✓</span>
                      )}
                    </button>
                  </div>
                ))}
              {missionRange < selectedDayMissions.length && (
                <button
                  onClick={() => {
                    const totalMissions = selectedDayMissions.length
                    if (missionRange === 0) {
                      setMissionRange(3)
                    } else {
                      setMissionRange(totalMissions)
                    }
                  }}
                  className='w-full text-sm text-amber-600 font-medium flex items-center justify-center mt-2'
                >
                  <ChevronDown className='w-6 h-6' />
                </button>
              )}
            </div>
          </CardOne>

          {/* Self-Reflection Section */}
          <CardOne className='mb-23 relative min-h-90'>
            {myStepsByLatestBlock?.data.steps.filter(
              (item: MyStepItem) => item.status === 'IN_PROGRESS' || item.status === 'COMPLETED'
            ).length === 0 && (
              <div className='absolute bg-white/40 backdrop-blur-xs top-0 left-0 z-20 w-full h-full rounded-2xl flex items-center justify-center'>
                <div className='flex flex-col items-center justify-center gap-3 font-bold'>
                  <p>진행중인 상담이 없어요.</p>
                  <p>상담이 진행되면 이곳에 나타납니다.</p>
                </div>
              </div>
            )}
            {userInfo?.data.type16_id === null && (
              <div className='absolute bg-white/40 backdrop-blur-xs top-0 left-0 z-20 w-full h-full rounded-2xl flex items-center justify-center'>
                <div className='flex flex-col items-center justify-center gap-5 font-bold'>
                  <div className='flex flex-col items-center justify-center'>
                    <p>타입 검사 진행 후, 본인에게 맞는</p>
                    <p>상담 커리큘럼이 표시됩니다.</p>
                  </div>
                  <button
                    className='h-10 w-32 bg-amber-500 text-sm font-bold text-white rounded-xl'
                    onClick={handleStartTypeCheck}
                  >
                    타입 검사
                  </button>
                </div>
              </div>
            )}
            {/* Questions Section */}
            <div className='flex flex-col mb-3 px-[10px]'>
              <h5 className='text-base font-bold text-gray-800 mb-4'>{myStepsByLatestBlock?.data.block.desc}</h5>
              <div className='h-3 bg-gray-100 rounded-full overflow-hidden mb-2'>
                <div
                  className='h-full bg-gradient-to-r from-amber-400 to-orange-400 rounded-full transition-all duration-500'
                  style={{
                    width: `${((myStepsByLatestBlock?.data.steps.filter((item: MyStepItem) => item.status === 'COMPLETED').length || 0) / (myStepsByLatestBlock?.data.steps.length || 1)) * 100}%`
                  }}
                />
              </div>
              <p className='text-xs text-gray-500'>
                총 {myStepsByLatestBlock?.data.steps.length}개의 도전 중{' '}
                {myStepsByLatestBlock?.data.steps.filter((item: MyStepItem) => item.status === 'COMPLETED').length}개
                완료!{' '}
                {
                  myStepsByLatestBlock?.data.steps.filter(
                    (item: MyStepItem) => item.status === 'NOT_STARTED' || item.status === 'IN_PROGRESS'
                  ).length
                }
                개 남았어요.
              </p>
            </div>

            {/* Question List */}
            <div className='space-y-3.5'>
              {/* 질문 목록 */}
              {visibleQuestions.map((item: any, index: number) => (
                <div
                  key={`home-consult-step-${item?.user_step_id ?? Math.random() * 1000000}`}
                  className='flex items-center justify-between gap-2'
                >
                  <div className='flex items-center gap-3 flex-1 min-w-0'>
                    <button
                      onClick={e => {
                        e.stopPropagation()
                      }}
                      className='w-8 h-8 rounded-full bg-amber-100 hover:bg-amber-200 flex items-center justify-center flex-shrink-0 transition-colors'
                    >
                      <Star className='w-4 h-4 fill-amber-500 text-amber-500' />
                    </button>
                    <div className='flex-1 min-w-0'>
                      <p className='text-gray-800 text-sm font-medium truncate'>{item.name}</p>
                      <p className='text-gray-500 text-xs mt-0.5'>{item.desc}</p>
                    </div>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap flex-shrink-0 ${getStatusColor(item.status)}`}
                  >
                    {item.status === 'COMPLETED'
                      ? '달성했어요'
                      : item.status === 'IN_PROGRESS'
                        ? '진행중'
                        : item.status === 'NOT_STARTED' && visibleQuestions.at(index - 1)?.status !== 'COMPLETED'
                          ? '곧 만나요'
                          : '시작해요'}
                  </span>
                </div>
              ))}

              {/* 다음 항목 개수 표시 */}
              {remainingNextCount > 0 && (
                <p className='w-full mt-4 text-center py-2 text-xs text-gray-500'>{remainingNextCount} more</p>
              )}
            </div>
          </CardOne>
        </div>
      </PullToRefresh>
    </>
  )
}

export default HomePage
